import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAdmin } from '../../context/AdminContext';

const navItems = [
  { to: '/admin/dashboard',    label: 'Dashboard',        icon: '📊' },
  { to: '/admin/inquiries',    label: 'Inquiries',        icon: '📋' },
  { to: '/admin/blog',         label: 'Blog Posts',       icon: '✍️' },
  { to: '/admin/alerts',       label: 'Alerts & Offers',  icon: '🔔' },
  { to: '/admin/destinations', label: 'Destinations',     icon: '🗺️' },
  { to: '/admin/feedback', label: 'Feedback', icon: '💬' }
];

export default function AdminLayout({ children }) {
  const { admin, logout }       = useAdmin();
  const navigate                = useNavigate();
  const location                = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Close sidebar on route change
  useEffect(function() { setSidebarOpen(false); }, [location]);

  // Lock body scroll when sidebar open on mobile
  useEffect(function() {
    if (sidebarOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return function() { document.body.style.overflow = ''; };
  }, [sidebarOpen]);

  function handleLogout() {
    logout();
    navigate('/admin/login');
  }

  function linkStyle(isActive) {
    return {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '11px 14px',
      borderRadius: 8,
      fontWeight: 600,
      fontSize: '0.9rem',
      textDecoration: 'none',
      background: isActive ? 'var(--bj-clay)' : 'transparent',
      color: isActive ? '#fff' : 'var(--bj-espresso)',
      transition: 'background 0.15s, color 0.15s'
    };
  }

  const sidebarContent = (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>

      {/* Logo / brand */}
      <div style={{ padding: '20px 16px 16px', borderBottom: '1px solid var(--bj-sand)', marginBottom: 8 }}>
        <img src="/logo.png" alt="Bluejay Travels" style={{ height: 44, width: 'auto', marginBottom: 10 }} />
        <p style={{ margin: 0, fontSize: '0.78rem', fontWeight: 700, opacity: 0.6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Admin Portal
        </p>
        <p style={{ margin: '4px 0 0', fontSize: '0.82rem', opacity: 0.7 }}>
          {admin?.name} · <span style={{ textTransform: 'capitalize' }}>{admin?.role}</span>
        </p>
      </div>

      {/* Nav links */}
      <nav style={{ flex: 1, padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 4, overflowY: 'auto' }}>
        {navItems.map(function(item) {
          if (item.to === '/admin/destinations' && admin?.role !== 'superadmin' && !admin?.permissions?.manageDestinations) return null;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              style={function(s) { return linkStyle(s.isActive); }}
            >
              <span style={{ fontSize: '1rem', flexShrink: 0 }}>{item.icon}</span>
              {item.label}
            </NavLink>
          );
        })}
        {admin?.role === 'superadmin' && (
          <NavLink to="/admin/users" style={function(s) { return linkStyle(s.isActive); }}>
            <span style={{ fontSize: '1rem', flexShrink: 0 }}>👥</span>
            Admin Users
          </NavLink>
        )}

        {/* Divider */}
        <div style={{ height: 1, background: 'var(--bj-sand)', margin: '8px 2px' }} />

        {/* View Website */}
        <a
          href="/"
          target="_blank"
          rel="noreferrer"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '11px 14px',
            borderRadius: 8,
            fontWeight: 600,
            fontSize: '0.9rem',
            textDecoration: 'none',
            color: 'var(--bj-clay)',
            background: 'rgba(63,114,175,0.1)',
            border: '1.5px solid rgba(63,114,175,0.25)',
            transition: 'background 0.15s'
          }}
          onMouseEnter={function(e) { e.currentTarget.style.background = 'rgba(63,114,175,0.2)'; }}
          onMouseLeave={function(e) { e.currentTarget.style.background = 'rgba(63,114,175,0.1)'; }}
        >
          <span style={{ fontSize: '1rem', flexShrink: 0 }}>🌐</span>
          View Website
        </a>
      </nav>

      {/* Logout */}
      <div style={{ padding: '12px 12px 20px' }}>
        <button
          onClick={handleLogout}
          style={{
            width: '100%', padding: '10px 14px',
            borderRadius: 8, border: '1.5px solid var(--bj-sand)',
            background: 'transparent', cursor: 'pointer',
            fontFamily: 'var(--font-body)', fontWeight: 600,
            fontSize: '0.9rem', color: 'var(--bj-espresso)',
            display: 'flex', alignItems: 'center', gap: 8,
            transition: 'background 0.15s'
          }}
          onMouseEnter={function(e) { e.currentTarget.style.background = 'var(--bj-cream-soft)'; }}
          onMouseLeave={function(e) { e.currentTarget.style.background = 'transparent'; }}
        >
          🚪 Log Out
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bj-cream)' }}>

      {/* ── DESKTOP SIDEBAR ── */}
      <aside style={{
        width: 240, flexShrink: 0,
        background: 'var(--bj-cream-soft)',
        borderRight: '1px solid var(--bj-sand)',
        position: 'sticky', top: 0, height: '100vh',
        overflowY: 'auto'
      }} className="desktop-only">
        {sidebarContent}
      </aside>

      {/* ── MOBILE OVERLAY ── */}
      {sidebarOpen && (
        <div
          onClick={function() { setSidebarOpen(false); }}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(17,45,78,0.45)',
            backdropFilter: 'blur(2px)',
            zIndex: 200
          }}
        />
      )}

      {/* ── MOBILE SIDEBAR DRAWER ── */}
      <aside style={{
        position: 'fixed', top: 0, left: 0, bottom: 0,
        width: 260,
        background: 'var(--bj-cream-soft)',
        borderRight: '1px solid var(--bj-sand)',
        zIndex: 201,
        overflowY: 'auto',
        transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'transform 0.3s cubic-bezier(.4,0,.2,1)',
        boxShadow: sidebarOpen ? '4px 0 24px rgba(17,45,78,0.15)' : 'none',
        display:'block'
      }} className="mobile-only">
        {/* Close button */}
        <button
          onClick={function() { setSidebarOpen(false); }}
          style={{
            position: 'absolute', top: 12, right: 12,
            background: 'none', border: 'none',
            fontSize: '1.2rem', cursor: 'pointer',
            color: 'var(--bj-espresso)', padding: 6
          }}
        >
          ✕
        </button>
        {sidebarContent}
      </aside>

      {/* ── MAIN CONTENT ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>

        {/* Mobile top bar */}
        <div style={{
          display: 'none',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          background: 'var(--bj-cream-soft)',
          borderBottom: '1px solid var(--bj-sand)',
          position: 'sticky', top: 0, zIndex: 100
        }} className="mobile-topbar">
          <button
            onClick={function() { setSidebarOpen(true); }}
            style={{
              background: 'none', border: 'none',
              cursor: 'pointer', padding: 6,
              display: 'flex', flexDirection: 'column',
              gap: 4
            }}
            aria-label="Open menu"
          >
            <span style={{ display: 'block', width: 22, height: 2, background: 'var(--bj-espresso)', borderRadius: 2 }} />
            <span style={{ display: 'block', width: 22, height: 2, background: 'var(--bj-espresso)', borderRadius: 2 }} />
            <span style={{ display: 'block', width: 22, height: 2, background: 'var(--bj-espresso)', borderRadius: 2 }} />
          </button>
          <img src="/logo.png" alt="Bluejay Admin" style={{ height: 36, width: 'auto' }} />
          <div style={{ width: 34 }} />
        </div>

        <main style={{ flex: 1, padding: '28px 24px', minWidth: 0 }}>
          {children}
        </main>
      </div>

      {/* Mobile topbar CSS */}
      <style>{`
        @media (max-width: 768px) {
          .mobile-topbar { display: flex !important; }
          .desktop-only { display: none !important; }
          .mobile-only { display: block !important; }
        }
        @media (min-width: 769px) {
          .mobile-topbar { display: none !important; }
          .mobile-only { display: none !important; }
          .desktop-only { display: block !important; }
        }
      `}</style>
    </div>
  );
}